/**
 * @file dev-mock-customer.js
 * @description Local-development-only customer simulation.
 * @module GniibPromotions.DevMockCustomer
 *
 * Customer login cannot be completed through `shopify theme dev`: Shopify's
 * storefront auth controller returns 401 for any /account/login POST arriving
 * through the CLI proxy. That is upstream of the theme, and is unaffected by
 * hostname, Origin/Referer, or the hCaptcha spam-protection setting.
 *
 * This module fakes the client-side customer signals the promotion system
 * reads, so promotions can be exercised locally without logging in.
 *
 * Usage:
 *   http://127.0.0.1:9292/pages/<promo-page>?mock_role=fubon
 *   optional: &mock_customer_id=123456&mock_tags=vip,staff
 *
 * Roles match ROLE_TO_HANDLER in promotion-config.js (staff, gniib_staff,
 * microsigx_staff, skhwc_staff, fubon, citibank, cumc, liuyang, ibd_group,
 * kwh_macau, space_vip, partner_staff).
 *
 * This file ships to the store (Liquid renders on Shopify's servers, so the
 * template can't tell local development from production), but it is inert
 * anywhere except 127.0.0.1/localhost and does nothing without ?mock_role=.
 *
 * NOTE: this only affects client-side logic. Liquid's `customer` object is
 * still nil, so anything rendered server-side (header account links, the
 * `{%- if customer -%}` blocks in layout/theme.liquid) stays logged-out.
 */
(function (global) {
  'use strict';

  // Hard gate: only ever active on the local development server.
  var host = global.location.hostname;
  if (host !== '127.0.0.1' && host !== 'localhost' && host !== '[::1]') return;

  var params = new URLSearchParams(global.location.search);
  var role = params.get('mock_role');
  if (!role) return;

  var id = params.get('mock_customer_id') || '9999999999';
  var tagsParam = params.get('mock_tags') || '';
  var tags = tagsParam
    ? tagsParam.split(',').map(function (tag) { return tag.trim(); })
    : [];

  // Mirror the values onto the meta tags layout/theme.liquid would have
  // rendered, so anything else reading them sees the same customer.
  [
    ['customer-id', id],
    ['customer-role', role],
    ['customer-tags', tagsParam]
  ].forEach(function (pair) {
    var el = document.querySelector('meta[name="' + pair[0] + '"]');
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', pair[0]);
      document.head.appendChild(el);
    }
    el.setAttribute('content', pair[1]);
  });

  // CustomerService.getCustomerId() reads window.ShopifyAnalytics before it
  // falls back to the meta tag, and Shopify's analytics script may overwrite
  // that object after this runs, so override the accessors directly.
  var CustomerService = global.GniibPromotions && global.GniibPromotions.CustomerService;
  if (CustomerService) {
    CustomerService.getCustomerId = function () { return id; };
    CustomerService.getCustomerRole = function () { return role.toLowerCase(); };
    CustomerService.getCustomerTags = function () { return tags.slice(); };
  } else {
    console.warn('[dev-mock-customer] CustomerService not loaded; load this after promotion-services.js');
  }

  // layout/theme.liquid decides access server-side from customer.metafields.custom.role,
  // which is still nil without a real login, so restricted pages render the access
  // dialog over the content. Dismiss it, the same way the snippet already does for the
  // URL-based liuyang role.
  var overlay = document.getElementById('access-restriction-overlay');
  if (overlay) {
    overlay.style.display = 'none';
  }

  console.info(
    '[dev-mock-customer] simulating role=%s id=%s tags=%s',
    role,
    id,
    tagsParam || '(none)'
  );
})(window);
